import React from 'react';
import {
  calculateCompositePricing,
  calculateCompositePricingDefaults,
  CompositePricingInput,
} from '../../utils/feeCategoryCompositePricing';

export type CompositePricingField =
  | 'basePrice'
  | 'vatAmount'
  | 'extraFee1Percent'
  | 'extraFee1Amount'
  | 'extraFee1VatPercent'
  | 'extraFee1VatAmount'
  | 'extraFee1Fixed'
  | 'extraFee2Percent'
  | 'extraFee2Amount'
  | 'extraFee2VatPercent'
  | 'extraFee2VatAmount'
  | 'extraFee2Fixed'
  | 'totalUnitPrice'
  | 'price';

export type CompositePricingTextField = 'extraFee1Name' | 'extraFee2Name';

export interface CompositePricingValues extends CompositePricingInput {
  extraFee1Name?: string | null;
  extraFee2Name?: string | null;
}

interface CompositePricingFieldsProps {
  values: CompositePricingValues;
  vatPercentage: number;
  onChange: (field: CompositePricingField, value: number | null) => void;
  onTextChange?: (field: CompositePricingTextField, value: string) => void;
  onVatPercentageChange?: (value: number) => void;
  hideVatPercentage?: boolean;
  formatNumberDisplay: (value: number | string) => string;
  parseNumberInput: (value: string) => string;
  t: (key: string) => string;
  compact?: boolean;
}

const CompositePricingFields: React.FC<CompositePricingFieldsProps> = ({
  values,
  vatPercentage,
  onChange,
  onTextChange,
  onVatPercentageChange,
  hideVatPercentage = false,
  formatNumberDisplay,
  parseNumberInput,
  t,
  compact = false,
}) => {
  const breakdown = calculateCompositePricing({
    ...values,
    vatPercentage,
  });

  const calculatedDefaults = calculateCompositePricingDefaults({
    ...values,
    vatPercentage,
  });

  const displayTotal = values.totalUnitPrice ?? calculatedDefaults.totalUnitPrice;

  const numberInput = (
    field: CompositePricingField,
    value: number | undefined | null,
    placeholder?: string,
  ) => (
    <input
      type="text"
      value={formatNumberDisplay(value ?? '')}
      onChange={(e) => {
        const raw = parseNumberInput(e.target.value);
        onChange(field, raw === '' ? null : parseFloat(raw) || 0);
      }}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
      placeholder={placeholder}
    />
  );

  const percentInput = (
    field: CompositePricingField,
    value: number | undefined | null,
  ) => (
    <input
      type="number"
      step="0.01"
      min="0"
      max="100"
      value={value ?? 0}
      onChange={(e) => onChange(field, parseFloat(e.target.value) || 0)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  );

  const textInput = (
    field: CompositePricingTextField,
    value: string | null | undefined,
    placeholder?: string,
  ) => (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onTextChange?.(field, e.target.value)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
      placeholder={placeholder}
    />
  );

  const sectionClass = compact
    ? 'space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200'
    : 'space-y-4 p-4 bg-amber-50 rounded-lg border border-amber-200';

  return (
    <div className={sectionClass}>
      {!compact && (
        <div>
          <h4 className="text-lg font-medium text-amber-900">{t('compositePricingTitle')}</h4>
          <p className="text-sm text-amber-800 mt-1">{t('compositePricingHint')}</p>
        </div>
      )}

      <div className={`grid grid-cols-1 ${hideVatPercentage ? 'md:grid-cols-2' : 'md:grid-cols-2'} gap-4`}>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('unitPriceLabel')}</label>
          {numberInput('basePrice', values.basePrice, t('pricePlaceholder'))}
        </div>
        {!hideVatPercentage && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('vatPercentage')}</label>
            {onVatPercentageChange ? (
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={vatPercentage}
                onChange={(e) => onVatPercentageChange(parseFloat(e.target.value) || 0)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <input
                type="number"
                value={vatPercentage}
                readOnly
                className="w-full border border-gray-200 bg-gray-100 rounded-lg px-3 py-2 text-gray-600"
              />
            )}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('vatAmountLabel')}</label>
          {numberInput('vatAmount', values.vatAmount ?? calculatedDefaults.vatAmount)}
          <p className="text-xs text-gray-500 mt-1">{t('calculatedValue')}: {formatNumberDisplay(calculatedDefaults.vatAmount)}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('priceWithVatLabel')}</label>
          <input
            type="text"
            readOnly
            value={formatNumberDisplay(breakdown.priceWithVat)}
            className="w-full border border-gray-200 bg-gray-100 rounded-lg px-3 py-2 text-gray-700"
          />
        </div>
      </div>

      <div className="border-t border-amber-200 pt-4">
        <h5 className="text-sm font-semibold text-gray-800 mb-3">{t('extraFeeGroup1Title')}</h5>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeGroupNameLabel')}</label>
            {textInput('extraFee1Name', values.extraFee1Name, t('extraFeeGroupNamePlaceholder'))}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeePercentLabel')}</label>
            {percentInput('extraFee1Percent', values.extraFee1Percent)}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeAmountLabel')}</label>
            {numberInput('extraFee1Amount', values.extraFee1Amount ?? calculatedDefaults.extraFee1Amount)}
            <p className="text-xs text-gray-500 mt-1">{t('calculatedValue')}: {formatNumberDisplay(calculatedDefaults.extraFee1Amount)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeVatPercentLabel')}</label>
            {percentInput('extraFee1VatPercent', values.extraFee1VatPercent)}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeVatAmountLabel')}</label>
            {numberInput('extraFee1VatAmount', values.extraFee1VatAmount ?? calculatedDefaults.extraFee1VatAmount)}
            <p className="text-xs text-gray-500 mt-1">{t('calculatedValue')}: {formatNumberDisplay(calculatedDefaults.extraFee1VatAmount)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeFixedLabel')}</label>
            {numberInput('extraFee1Fixed', values.extraFee1Fixed ?? 0)}
          </div>
        </div>
      </div>

      <div className="border-t border-amber-200 pt-4">
        <h5 className="text-sm font-semibold text-gray-800 mb-3">{t('extraFeeGroup2Title')}</h5>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeGroupNameLabel')}</label>
            {textInput('extraFee2Name', values.extraFee2Name, t('extraFeeGroupNamePlaceholder'))}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeePercentLabel')}</label>
            {percentInput('extraFee2Percent', values.extraFee2Percent)}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeAmountLabel')}</label>
            {numberInput('extraFee2Amount', values.extraFee2Amount ?? calculatedDefaults.extraFee2Amount)}
            <p className="text-xs text-gray-500 mt-1">{t('calculatedValue')}: {formatNumberDisplay(calculatedDefaults.extraFee2Amount)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeVatPercentLabel')}</label>
            {percentInput('extraFee2VatPercent', values.extraFee2VatPercent)}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeVatAmountLabel')}</label>
            {numberInput('extraFee2VatAmount', values.extraFee2VatAmount ?? calculatedDefaults.extraFee2VatAmount)}
            <p className="text-xs text-gray-500 mt-1">{t('calculatedValue')}: {formatNumberDisplay(calculatedDefaults.extraFee2VatAmount)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeFixedLabel')}</label>
            {numberInput('extraFee2Fixed', values.extraFee2Fixed ?? 0)}
          </div>
        </div>
      </div>

      <div className="border-t border-amber-300 pt-4">
        <label className="block text-sm font-medium text-gray-800 mb-2">{t('totalUnitPriceAfterTax')}</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {numberInput('totalUnitPrice', displayTotal)}
          <div className="flex items-end">
            <p className="text-sm text-gray-600 pb-2">
              {t('calculatedValue')}: <span className="font-semibold text-gray-900">{formatNumberDisplay(calculatedDefaults.totalUnitPrice)}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompositePricingFields;
